import { useEffect, useState } from 'react';

import { useNavigate } from 'react-router-dom';

import { useQueryClient } from '@tanstack/react-query';
import PropTypes from 'prop-types';
import { BsArrowLeft, BsXCircle } from 'react-icons/bs';

import Button from '#globalComponents/Button/Button';
import Card from '#globalComponents/Card/Card';
import Page from '#globalComponents/Page/Page';
import { useErrorStore } from '#lib/stores/errorStore';

import ManualPipelineForm from '../../components/settings/NewPipelineModal/ManualPipelineForm';
import { useAnalyzeCompany, useGeneratePipeline } from '../../models/onboarding/mutations';
import { usePipelineGenerationStatus } from '../../models/onboarding/queries';
import { pipelinesKeys } from '../../models/pipelines/keys';

import AnalyzingView from './AnalyzingView';
import GeneratingView from './GeneratingView';
import AnalysisReviewStep from './steps/AnalysisReviewStep';
import CompanyInputStep from './steps/CompanyInputStep';
import StrategySuccessView from './StrategySuccessView';

/**
 * Two-step onboarding wizard with success summary:
 *
 * 1. CompanyInputStep: user enters company name + website URL
 *    → POST /analyze → poll until analysisStatus='completed'
 * 2. AnalysisReviewStep: user reviews/edits the AI analysis
 *    → POST /generate → poll until status='completed'
 * 3. StrategySuccessView: shows generated strategy + pipeline summary
 *    → user clicks "Zur Pipeline" → navigate to /crm/pipeline
 *
 * State machine: input → analyzing → reviewAnalysis → generating → success
 */

const FailureView = ({ title, reason, retryLabel, onRetry, onNavigatePipeline }) => (
	<div className="flex items-center justify-center min-h-[60vh] px-3 sm:px-6">
		<Card className="max-w-xl w-full p-4 sm:p-8">
			<div className="flex items-start gap-3">
				<div className="h-10 w-10 rounded-control bg-warning-200 text-warning-700 flex items-center justify-center flex-shrink-0">
					<BsXCircle className="w-5 h-5" />
				</div>
				<div>
					<h2 className="text-lg font-semibold text-text mb-1">{title}</h2>
					<p className="text-sm text-bgBase-800 mb-2">{reason ?? 'Unbekannter Fehler.'}</p>
					<p className="text-sm text-text">{retryLabel}</p>
				</div>
			</div>
			<div className="flex justify-end gap-2 mt-6">
				<Button variant="outline" onClick={onNavigatePipeline}>
					Zur Pipeline
				</Button>
				<Button variant="solid" onClick={onRetry}>
					Erneut versuchen
				</Button>
			</div>
		</Card>
	</div>
);

FailureView.propTypes = {
	title: PropTypes.string.isRequired,
	reason: PropTypes.string,
	retryLabel: PropTypes.string.isRequired,
	onRetry: PropTypes.func.isRequired,
	onNavigatePipeline: PropTypes.func.isRequired,
};

/** Centered full-height card wrapper used by the analyzing / generating screens. */
const ProgressScreen = ({ children }) => (
	<div className="flex items-center justify-center min-h-[60vh] px-3 sm:px-6">
		<Card className="max-w-xl w-full p-6 sm:p-10">{children}</Card>
	</div>
);

ProgressScreen.propTypes = {
	children: PropTypes.node.isRequired,
};

/**
 * The 2-step wizard (company input → analysis review) with progress bar and
 * navigation. Step 1 also offers a manual-creation shortcut.
 */
const WizardStep = ({ phase, values, onFieldChange, onManual, onBack, onPrimary, primaryLoading }) => {
	const isStep1 = phase === 'input';
	const canProceed = isStep1 ? CompanyInputStep.isValid(values) : AnalysisReviewStep.isValid(values);
	const progressPct = isStep1 ? 25 : 75;

	return (
		<div className="flex items-start justify-center min-h-[70vh] px-3 py-4 sm:px-6 sm:py-8">
			<Card className="max-w-2xl w-full p-4 sm:p-8">
				{/* Progress */}
				<div className="mb-6">
					<div className="flex items-center justify-between text-xs text-bgBase-800 mb-2">
						<span>Schritt {isStep1 ? 1 : 2} von 2</span>
						<span>{progressPct}%</span>
					</div>
					<div className="h-1.5 rounded-full bg-bgBase-400 overflow-hidden">
						<div
							className="h-full bg-primary-500 transition-[width] duration-300"
							style={{ width: `${progressPct}%` }}
						/>
					</div>
					<div className="flex justify-between mt-2 text-[11px] uppercase tracking-wider">
						<span className={isStep1 ? 'text-text font-semibold' : 'text-primary-600'}>Unternehmen</span>
						<span className={isStep1 ? 'text-bgBase-800' : 'text-text font-semibold'}>Analyse prüfen</span>
					</div>
				</div>

				{/* Step body */}
				{isStep1 ? (
					<>
						<CompanyInputStep values={values} onChange={onFieldChange} />
						<div className="mt-6 text-center">
							<button
								type="button"
								onClick={onManual}
								className="text-sm text-bgBase-800 hover:text-text underline underline-offset-2"
							>
								Pipeline lieber manuell erstellen
							</button>
						</div>
					</>
				) : (
					<AnalysisReviewStep values={values} onChange={onFieldChange} />
				)}

				{/* Navigation */}
				<div className="flex items-center justify-between mt-6 sm:mt-8 pt-4 sm:pt-6 border-t border-bgBase-400">
					<Button type="button" variant="outline" onClick={onBack}>
						<BsArrowLeft className="h-4 w-4 sm:mr-2" />
						<span className="hidden sm:inline">Zurück</span>
					</Button>
					<Button
						className="flex-1 sm:flex-none ml-3 sm:ml-0"
						type="button"
						variant="solid"
						onClick={onPrimary}
						disabled={!canProceed}
						loading={primaryLoading}
					>
						{isStep1 ? 'Website analysieren' : 'Strategie generieren'}
					</Button>
				</div>
			</Card>
		</div>
	);
};

WizardStep.propTypes = {
	phase: PropTypes.oneOf(['input', 'reviewAnalysis']).isRequired,
	values: PropTypes.object.isRequired,
	onFieldChange: PropTypes.func.isRequired,
	onManual: PropTypes.func.isRequired,
	onBack: PropTypes.func.isRequired,
	onPrimary: PropTypes.func.isRequired,
	primaryLoading: PropTypes.bool,
};

async function runAnalyze({ analyzeCompany, values, setGenerationId, setPhase, showError }) {
	try {
		const res = await analyzeCompany.mutateAsync({
			customerWebsite: CompanyInputStep.getNormalizedWebsite(values),
		});
		setGenerationId(res.generation?.id ?? null);
		setPhase('analyzing');
	} catch (err) {
		showError({ error: err, title: 'Analyse konnte nicht gestartet werden' });
	}
}

async function runGenerate({ generatePipeline, generationId, values, setPhase, showError }) {
	try {
		await generatePipeline.mutateAsync({
			generationId,
			websiteAnalysis: values.websiteAnalysis,
		});
		setPhase('generating');
	} catch (err) {
		showError({ error: err, title: 'Pipeline-Generierung konnte nicht gestartet werden' });
	}
}

function isAnalysisInProgress(generation) {
	return !generation || generation.analysisStatus === 'pending' || generation.analysisStatus === 'analyzing';
}

function isGenerationInProgress(generation) {
	return !generation || generation.status === 'pending' || generation.status === 'generating';
}

const INITIAL_VALUES = {
	customerWebsite: '',
	websiteAnalysis: null,
};

const CrmOnboardingPage = () => {
	const navigate = useNavigate();
	const queryClient = useQueryClient();
	const analyzeCompany = useAnalyzeCompany();
	const generatePipeline = useGeneratePipeline();
	const showError = useErrorStore((s) => s.showError);

	const [phase, setPhase] = useState('input'); // input | analyzing | reviewAnalysis | generating | success
	const [values, setValues] = useState(INITIAL_VALUES);
	const [generationId, setGenerationId] = useState(null);

	const { data: generation } = usePipelineGenerationStatus(generationId);

	// React to analysis completion
	useEffect(() => {
		if (phase === 'analyzing' && generation?.analysisStatus === 'completed') {
			setValues((prev) => ({
				...prev,
				websiteAnalysis: generation.websiteAnalysis,
			}));
			setPhase('reviewAnalysis');
		}
	}, [phase, generation]);

	// React to pipeline generation completion → refresh pipelines (so CrmShell
	// sees the new one and won't bounce back here) and show the success view.
	useEffect(() => {
		if (phase === 'generating' && generation?.status === 'completed') {
			queryClient.invalidateQueries({ queryKey: pipelinesKeys.all });
			setPhase('success');
		}
	}, [phase, generation, queryClient]);

	const handleFieldChange = (patch) => setValues((prev) => ({ ...prev, ...patch }));

	const handleAnalyze = () => runAnalyze({ analyzeCompany, values, setGenerationId, setPhase, showError });
	const handleGenerate = () => runGenerate({ generatePipeline, generationId, values, setPhase, showError });

	const handleRetryAnalysis = () => {
		setGenerationId(null);
		setPhase('input');
	};

	const handleRetryGeneration = () => {
		setPhase('reviewAnalysis');
	};

	// Manual creation done → jump straight into the new pipeline (useCreatePipeline
	// already refreshes the pipelines cache, so CrmShell won't bounce back here).
	const handleManualCreated = (created) => {
		const pid = created?.pipeline?.id ?? created?.id;
		navigate(pid ? `/crm/pipeline?pipelineId=${pid}` : '/crm/pipeline', { replace: true });
	};

	// Success state
	if (phase === 'success' && generation) {
		return (
			<Page>
				<div className="flex items-start justify-center min-h-[70vh] px-3 py-4 sm:px-6 sm:py-8">
					<Card className="max-w-2xl w-full p-4 sm:p-8">
						<StrategySuccessView
							generation={generation}
							onNavigate={() => {
								const pid = generation.pipelineId;
								navigate(pid ? `/crm/pipeline?pipelineId=${pid}` : '/crm/pipeline', { replace: true });
							}}
						/>
					</Card>
				</div>
			</Page>
		);
	}

	// Manual creation
	if (phase === 'manual') {
		return (
			<Page>
				<div className="flex items-start justify-center min-h-[70vh] px-3 py-4 sm:px-6 sm:py-8">
					<Card className="max-w-2xl w-full p-4 sm:p-8">
						<ManualPipelineForm onCreated={handleManualCreated} onBack={() => setPhase('input')} />
					</Card>
				</div>
			</Page>
		);
	}

	// Analyzing state
	if (phase === 'analyzing') {
		return (
			<Page>
				{isAnalysisInProgress(generation) ? (
					<ProgressScreen>
						<AnalyzingView />
					</ProgressScreen>
				) : (
					<FailureView
						title="Die Website-Analyse ist fehlgeschlagen"
						reason={generation.failureReason}
						retryLabel="Prüfen Sie die URL und versuchen Sie es erneut."
						onRetry={handleRetryAnalysis}
						onNavigatePipeline={() => navigate('/crm/pipeline')}
					/>
				)}
			</Page>
		);
	}

	// Generating state
	if (phase === 'generating') {
		return (
			<Page>
				{isGenerationInProgress(generation) ? (
					<ProgressScreen>
						<GeneratingView />
					</ProgressScreen>
				) : (
					<FailureView
						title="Die KI-Generierung ist fehlgeschlagen"
						reason={generation.failureReason}
						retryLabel="Versuch es bitte erneut — oder wechsle direkt zur Pipeline-Ansicht und lege manuell eine Pipeline an."
						onRetry={handleRetryGeneration}
						onNavigatePipeline={() => navigate('/crm/pipeline')}
					/>
				)}
			</Page>
		);
	}

	// Wizard: Step 1 (input) or Step 2 (reviewAnalysis)
	const isStep1 = phase === 'input';
	return (
		<Page>
			<WizardStep
				phase={phase}
				values={values}
				onFieldChange={handleFieldChange}
				onManual={() => setPhase('manual')}
				onBack={() => (isStep1 ? navigate(-1) : setPhase('input'))}
				onPrimary={isStep1 ? handleAnalyze : handleGenerate}
				primaryLoading={analyzeCompany.isPending || generatePipeline.isPending}
			/>
		</Page>
	);
};

export default CrmOnboardingPage;
